// Phần tính thuần của màn kỳ lương: dựng đầu vào từ dữ liệu đã lưu của kỳ, tính, chữ ký đầu vào, điều
// kiện gửi duyệt, ma trận hiển thị và dữ liệu ghi xuống khi chốt số. Nhập, chốt và duyệt cùng dùng các
// hàm này nên luôn ra cùng một con số.
import { canonicalPayrollCode, isPayrollEditable } from './domain';
import { createInputHash } from './inputHash';
import { isAdditiveColumn } from './payrollSheet';
import { buildPayrollEmployeeInputs, createPeriodTemplateCompiler, runPayrollCalculation } from './periodInputs';

export const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

export function periodRange(period) {
    return { from: period.TU_NGAY, to: endOfDay(period.DEN_NGAY) };
}

export function isPeriodEditable(period) {
    return isPayrollEditable(period.TRANG_THAI);
}

/**
 * Dòng chi tiết kỳ của một nhân sự (dùng cả khi tạo kỳ thật lẫn khi dựng bảng xem trước lúc tạo kỳ).
 * employee: kết quả chọn mẫu { employeeId, employeeName, team, templateId }; employees: NHANSU để lấy tổ chính.
 */
export function buildDetailRow(maKy, employee, employees, inputs = {}) {
    const record = employees.find((item) => item.SO_HD === employee.employeeId);
    return {
        ID: `${maKy}|${employee.employeeId}`,
        BANG_ID: maKy,
        NHAN_SU_ID: employee.employeeId,
        HO_TEN_SNAPSHOT: employee.employeeName,
        TO_SNAPSHOT: record?.TO || employee.team || '',
        MAU_ID: employee.templateId,
        GIA_TRI_NHAP: inputs,
        GIA_TRI_TINH: {},
        GHI_DE: {},
        CHI_TIET_NANG_SUAT: [],
        TONG_LUONG: 0, TONG_UNG: 0, BH_NLD: 0, TNCN: 0, TONG_THUC_CHI: 0,
        GHI_CHU: '',
    };
}

// Giá trị nhập của một người: số đã lưu chồng số đang sửa; null nghĩa là xóa số đã nhập.
function mergeInputs(saved = {}, pending = {}) {
    const result = { ...saved };
    for (const [code, value] of Object.entries(pending)) {
        if (value === null || value === undefined || value === '') delete result[code];
        else result[code] = value;
    }
    return result;
}

function mergeOverrides(saved = {}, pending = {}) {
    const result = { ...saved };
    for (const [code, value] of Object.entries(pending)) {
        if (value === null) delete result[code];
        else result[code] = value;
    }
    return Object.fromEntries(Object.entries(result).map(([code, item]) => [code, { value: item.value, reason: item.reason, by: item.by ?? null, at: item.at ? new Date(item.at) : null }]));
}

/**
 * Tính cả kỳ từ dữ liệu đã lưu và phần đang sửa trên màn hình (pending). Ném lỗi khi mẫu hoặc công thức
 * hỏng (vd chia cho 0) để màn hình báo rõ biến nào.
 * pending: { inputs: { [soHd]: { [code]: number|null } }, overrides: { [soHd]: { [code]: {value, reason}|null } } }
 */
export function buildPeriodCalculation({ period, details, ref, templates, pending = {} }) {
    const templateById = new Map(templates.map((template) => [template.ID, template]));
    const compile = createPeriodTemplateCompiler(ref.variableCatalog, ref.base.formulaRows);
    const missingTemplate = details.filter((detail) => !templateById.has(detail.MAU_ID));
    if (missingTemplate.length) {
        throw new Error(`Mẫu ${[...new Set(missingTemplate.map((detail) => detail.MAU_ID))].join(', ')} của kỳ không còn tồn tại.`);
    }
    const inputs = buildPayrollEmployeeInputs({
        range: periodRange(period),
        employees: details.map((detail) => ({ SO_HD: detail.NHAN_SU_ID, HO_TEN: detail.HO_TEN_SNAPSHOT, TO: detail.TO_SNAPSHOT })),
        templateByEmployee: new Map(details.map((detail) => [detail.NHAN_SU_ID, templateById.get(detail.MAU_ID)])),
        variableCatalog: ref.variableCatalog,
        base: ref.base,
        manualByEmployee: new Map(details.map((detail) => [detail.NHAN_SU_ID, mergeInputs(detail.GIA_TRI_NHAP, pending.inputs?.[detail.NHAN_SU_ID])])),
        overridesByEmployee: new Map(details.map((detail) => [detail.NHAN_SU_ID, mergeOverrides(detail.GHI_DE, pending.overrides?.[detail.NHAN_SU_ID])])),
        productivityLinesByEmployee: new Map(details.map((detail) => [detail.NHAN_SU_ID, detail.CHI_TIET_NANG_SUAT ?? []])),
        compileTemplate: compile,
    });
    return { calculation: runPayrollCalculation(inputs), compile, templateById };
}

/**
 * Chữ ký mọi đầu vào đã lưu của kỳ. Chỉ chiếu đúng các trường tham gia tính lương: thêm một cột không
 * liên quan vào sheet không được làm mọi kỳ chưa duyệt phải chốt lại vô cớ.
 */
export function computePeriodInputHash({ period, details, ref, templates }) {
    const employeeIds = new Set(details.map((detail) => detail.NHAN_SU_ID));
    const usedTemplateIds = new Set(details.map((detail) => detail.MAU_ID));
    return createInputHash({
        period: { id: period.MA_KY, from: period.TU_NGAY, to: period.DEN_NGAY },
        details: details.map((detail) => ({
            employee: detail.NHAN_SU_ID,
            template: detail.MAU_ID,
            inputs: detail.GIA_TRI_NHAP ?? {},
            overrides: Object.fromEntries(Object.entries(detail.GHI_DE ?? {}).map(([code, item]) => [code, item.value])),
            productivity: (detail.CHI_TIET_NANG_SUAT ?? []).map((line) => [line.TO_PBNS_ID, line.MA_CONG_DOAN, line.DON_GIA, line.KHOI_LUONG, line.THANH_TIEN]),
        })),
        templates: templates.filter((template) => usedTemplateIds.has(template.ID)).map((template) => ({
            id: template.ID,
            columns: template.CHI_TIETS.map((column) => [column.BIEN_ID, column.CONG_THUC, column.THU_TU, column.BAT_BUOC, column.HIEN_THI, column.CHO_PHEP_NHAP]),
        })),
        variables: ref.variableCatalog.map((variable) => [variable.MA_BIEN, variable.LOAI_BIEN, variable.NGUON_DU_LIEU, variable.LOAI_DU_LIEU, variable.CONG_THUC]),
        constants: Array.from(ref.base.constantRows, ([code, row]) => [code, row.GIA_TRI]),
        formulas: Array.from(ref.base.formulaRows, ([code, row]) => [code, row.CONG_THUC]),
        agreements: Array.from(ref.base.agreementByEmployee.values())
            .filter((agreement) => employeeIds.has(agreement.NHAN_SU_ID))
            .map((agreement) => [agreement.ID, agreement.NHAN_SU_ID, agreement.MAU_ID, agreement.HIEU_LUC_TU, agreement.HIEU_LUC_DEN, agreement.GIA_TRI]),
        advances: ref.base.advances
            .filter((advance) => employeeIds.has(advance.NHAN_SU_ID))
            .map((advance) => [advance.MA_PHIEU, advance.NHAN_SU_ID, advance.NGAY_UNG, advance.SO_TIEN]),
        holidays: (ref.base.holidays ?? []).map((holiday) => [holiday.ID, holiday.TU_NGAY, holiday.DEN_NGAY, holiday.SO_NGAY_HUONG_LUONG, holiday.LOAI_NGAY, holiday.PHAM_VI, holiday.TO, holiday.NHAN_SU_ID]),
        leaves: (ref.base.leaveRequests ?? [])
            .filter((request) => employeeIds.has(request.NHAN_SU_ID))
            .map((request) => [request.MA_DON, request.NHAN_SU_ID, request.LOAI_PHEP, request.CHI_TIET_NGAY]),
    });
}

const WARNING_SUMMARY = {
    MISSING_AGREEMENT: (count) => `${count} nhân sự chưa có thỏa thuận lương hiệu lực nên các khoản theo thỏa thuận tính bằng 0.`,
    PAID_DAYS_EXCEED_STANDARD: (count) => `${count} nhân sự có ngày hưởng lương lớn hơn ngày công chuẩn.`,
    INVALID_MONTHLY_SALARY: (count) => `${count} nhân sự lương tháng thiếu mức lương tháng (MLT).`,
    INVALID_STANDARD_WORK_DAYS: (count) => `${count} nhân sự lương tháng thiếu ngày công chuẩn (NCC).`,
};

// Gộp cảnh báo cùng mã: 30 người thiếu thỏa thuận là một dòng, không phải 30 dòng giống nhau.
export function summarizeWarnings(warnings) {
    const byCode = new Map();
    for (const warning of warnings) {
        const group = byCode.get(warning.code) ?? { ...warning, count: 0, employeeIds: [] };
        group.count += 1;
        if (warning.employeeId) group.employeeIds.push(warning.employeeId);
        byCode.set(warning.code, group);
    }
    return Array.from(byCode.values()).map((group) => ({
        code: group.code,
        blocking: group.blocking,
        employeeIds: group.employeeIds,
        message: group.count > 1 && WARNING_SUMMARY[group.code] ? WARNING_SUMMARY[group.code](group.count) : group.message,
    }));
}

/**
 * Điều kiện gửi duyệt và duyệt, theo V2: phải chốt số và chữ ký còn khớp, không có lỗi chặn; cảnh báo
 * không chặn thì người gửi phải tick xác nhận.
 */
export function evaluateReadiness({ period, currentHash, calculation, refWarnings = [], unmatchedProductivity = 0 }) {
    const blocking = [];
    if (!period.CALCULATED_AT || !period.INPUT_HASH) blocking.push('Kỳ lương chưa chốt số.');
    else if (period.INPUT_HASH !== currentHash) blocking.push('Dữ liệu đã thay đổi sau lần chốt số gần nhất, hãy chốt số lại.');
    const calcWarnings = summarizeWarnings(calculation?.warnings ?? []);
    blocking.push(...calcWarnings.filter((warning) => warning.blocking).map((warning) => warning.message));
    blocking.push(...refWarnings.filter((warning) => warning.blocking).map((warning) => warning.message));
    const warnings = [
        ...calcWarnings.filter((warning) => !warning.blocking).map((warning) => warning.message),
        ...refWarnings.filter((warning) => !warning.blocking).map((warning) => warning.message),
        ...(unmatchedProductivity ? [`Có ${unmatchedProductivity} dòng năng suất chưa xác định người nhận (chưa gắn Số HĐ phụ trách) nên không tính vào lương ai.`] : []),
    ];
    return { blocking, warnings };
}

/** Dữ liệu ghi xuống CT_BANG_CONG_LUONG khi chốt số. Số gốc chỉ lưu cho biến bị ghi đè. */
export function buildDetailSnapshot(employeeResult) {
    const current = {};
    const original = {};
    for (const variable of employeeResult.variables) {
        current[variable.code] = variable.currentValue;
        if (variable.overridden) original[variable.code] = variable.originalValue;
    }
    return {
        GIA_TRI_TINH: { v: current, g: original },
        TONG_LUONG: employeeResult.totalGross,
        TONG_UNG: employeeResult.advanceDeduction,
        BH_NLD: employeeResult.insurance,
        TNCN: Math.round(employeeResult.values.TNCN ?? 0),
        TONG_THUC_CHI: employeeResult.netPay,
    };
}

// Số đã chốt của một người ở cùng dạng với kết quả tính, để ma trận dùng chung một đường hiển thị.
function snapshotVariables(detail) {
    const current = detail.GIA_TRI_TINH?.v ?? {};
    const original = detail.GIA_TRI_TINH?.g ?? {};
    const overrides = detail.GHI_DE ?? {};
    return new Map(Object.entries(current).map(([code, value]) => [code, {
        code,
        currentValue: value,
        originalValue: original[code] ?? value,
        overridden: Object.prototype.hasOwnProperty.call(original, code),
        overrideReason: overrides[code]?.reason ?? null,
    }]));
}

export function cellState(source, overridden) {
    if (overridden) return 'OVERRIDDEN';
    if (source === 'NHAP_KY') return 'INPUT';
    if (source === 'CONG_THUC') return 'FORMULA';
    return 'SOURCE';
}

/**
 * Ma trận hiển thị, mỗi mẫu một nhóm theo thứ tự mẫu của kỳ. Ô nhập hiện đúng số đang gõ (kể cả chưa
 * lưu); ô khác hiện số vừa tính. useSnapshot: hiện số đã chốt lưu trong kỳ (kỳ đã gửi hoặc đã duyệt),
 * không tính lại theo dữ liệu nguồn có thể đã đổi sau đó.
 */
export function buildWorkbenchGroups({ period, details, calculation, compile, templateById, variableByCode, pendingInputs = {}, editable, useSnapshot = false }) {
    const resultByEmployee = new Map((calculation?.employees ?? []).map((employee) => [employee.employeeId, employee]));
    const order = [...(period.MAU_IDS ?? []), ...details.map((detail) => detail.MAU_ID)];
    const templateIds = order.filter((id, index) => order.indexOf(id) === index && details.some((detail) => detail.MAU_ID === id));
    return templateIds.map((templateId) => {
        const template = templateById.get(templateId);
        const compiled = compile(template);
        const columns = compiled.columns
            .filter((column) => !column.dependencyOnly && column.visible)
            .sort((left, right) => left.order - right.order)
            .map((column) => {
                const variable = variableByCode.get(column.code);
                return {
                    code: column.code,
                    name: variable?.TEN_BIEN || column.name || column.code,
                    unit: variable?.DON_VI || '',
                    group: variable?.NHOM || 'Khác',
                    dataType: variable?.LOAI_DU_LIEU || 'TIEN',
                    source: column.source,
                    formula: column.formula || null,
                    allowInput: column.allowInput,
                    additive: isAdditiveColumn(column.code, variable?.LOAI_DU_LIEU || 'TIEN'),
                };
            });
        const rows = details
            .filter((detail) => detail.MAU_ID === templateId)
            .sort((left, right) => String(left.TO_SNAPSHOT).localeCompare(String(right.TO_SNAPSHOT), 'vi') || String(left.HO_TEN_SNAPSHOT).localeCompare(String(right.HO_TEN_SNAPSHOT), 'vi'))
            .map((detail) => {
                const result = resultByEmployee.get(detail.NHAN_SU_ID);
                const variables = useSnapshot
                    ? snapshotVariables(detail)
                    : new Map((result?.variables ?? []).map((variable) => [canonicalPayrollCode(variable.code), variable]));
                const pending = pendingInputs[detail.NHAN_SU_ID] ?? {};
                const cells = Object.fromEntries(columns.map((column) => {
                    const variable = variables.get(column.code);
                    const isPending = Object.prototype.hasOwnProperty.call(pending, column.code);
                    const savedInput = detail.GIA_TRI_NHAP?.[column.code];
                    const inputValue = isPending ? pending[column.code] : savedInput;
                    return [column.code, {
                        value: column.allowInput && !useSnapshot ? (inputValue ?? null) : (variable?.currentValue ?? null),
                        original: variable?.originalValue ?? null,
                        overridden: Boolean(variable?.overridden),
                        overrideReason: variable?.overrideReason ?? null,
                        state: cellState(column.source, Boolean(variable?.overridden)),
                        editable: Boolean(editable && column.allowInput),
                        dirty: isPending,
                    }];
                }));
                return {
                    detailId: detail.ID,
                    soHd: detail.NHAN_SU_ID,
                    hoTen: detail.HO_TEN_SNAPSHOT,
                    to: detail.TO_SNAPSHOT,
                    cells,
                    result,
                    warnings: result?.warnings ?? [],
                };
            });
        const totals = Object.fromEntries(columns.map((column) => [
            column.code,
            column.additive ? rows.reduce((sum, row) => sum + (Number(row.cells[column.code].value) || 0), 0) : null,
        ]));
        return { templateId, templateName: template.TEN_MAU, compiled, columns, rows, totals };
    });
}

/**
 * Đọc file Excel nhập công: mỗi dòng một nhân sự (theo SỐ HĐ), mỗi cột một mã biến được phép nhập của mẫu
 * người đó. Ô trống bỏ qua; ô có chữ báo lỗi chứ không coi là 0.
 * sheetRows: object theo tiêu đề cột; parseNumber: hàm đọc số chấp nhận 1,5 và 1.5
 */
export function parseInputImportRows(sheetRows, { details, inputCodesByTemplate, parseNumber }) {
    const detailBySoHd = new Map(details.map((detail) => [String(detail.NHAN_SU_ID), detail]));
    const changes = {};
    const errors = [];
    sheetRows.forEach((row, index) => {
        const line = index + 2;
        const soHd = String(row['SỐ HĐ'] ?? '').trim();
        if (!soHd) return;
        const detail = detailBySoHd.get(soHd);
        if (!detail) {
            errors.push({ line, message: `Số HĐ ${soHd} không có trong kỳ lương này.` });
            return;
        }
        const allowed = inputCodesByTemplate.get(detail.MAU_ID) ?? [];
        for (const code of allowed) {
            const raw = row[code];
            if (raw === undefined || raw === null || String(raw).trim() === '') continue;
            const value = parseNumber(raw, null);
            if (value === null || Number.isNaN(value)) {
                errors.push({ line, message: `${soHd}: ${code} = "${raw}" không phải số.` });
                continue;
            }
            changes[soHd] = { ...(changes[soHd] ?? {}), [code]: value };
        }
    });
    return { changes, errors };
}

/**
 * So danh sách nhân sự của kỳ với kết quả chọn mẫu hiện tại: ai mới vào, ai không còn thuộc kỳ, ai đổi
 * mẫu. Dùng khi có người mới vào làm sau khi đã tạo kỳ.
 */
export function diffPeriodEmployees(details, resolved) {
    const detailBySoHd = new Map(details.map((detail) => [detail.NHAN_SU_ID, detail]));
    const resolvedBySoHd = new Map(resolved.employees.map((employee) => [employee.employeeId, employee]));
    return {
        added: resolved.employees.filter((employee) => !detailBySoHd.has(employee.employeeId)),
        removed: details.filter((detail) => !resolvedBySoHd.has(detail.NHAN_SU_ID)),
        changed: resolved.employees
            .filter((employee) => detailBySoHd.has(employee.employeeId) && detailBySoHd.get(employee.employeeId).MAU_ID !== employee.templateId)
            .map((employee) => ({ ...employee, previousTemplateId: detailBySoHd.get(employee.employeeId).MAU_ID })),
    };
}
